<template>
  <div class="partners-section" v-if="enabled && logos.length">
    <div class="partners-header">
      <!--
        Título com duas partes editáveis em Personalizar (Modo Edição):
        - clicando em "Nossas" ou em "Marcas Parceiras" ajusta cada parte (cor ou
          degradê de duas cores);
        - "Selecionar o elemento pai" (o h3) ajusta o título inteiro, com um único
          degradê de duas cores passando pelas duas partes ("texto junto").
      -->
      <h3
        ref="title"
        class="partners-title"
        :class="{ 'is-joint': joint }"
      ><span class="partners-title-a">Nossas</span> <span class="partners-title-b">Marcas Parceiras</span></h3>
      <p class="partners-subtitle">Trabalhamos com as melhores marcas de ferramentas do mercado</p>
    </div>
    <div class="scrolling-logos" ref="viewport">
      <!--
        Loop infinito sem emenda: o trilho tem DOIS grupos idênticos e a animação
        desloca exatamente um grupo (-50%). Cada grupo repete o conjunto de logos
        quantas vezes forem necessárias para ser mais largo que a tela (ver
        updateRepeat); assim nunca aparece espaço vazio entre o fim de um ciclo e o
        início do próximo, qualquer que seja o número de logos ou a largura da tela.
      -->
      <div class="logos-slide" ref="track" :style="trackStyle">
        <div
          v-for="g in 2"
          :key="'grupo-' + g"
          class="logos-group"
          :aria-hidden="g === 2 ? 'true' : null"
          :ref="g === 1 ? 'group' : null"
        >
          <div
            v-for="(logo, index) in groupLogos"
            :key="g + '-' + index"
            class="partner-card m-3"
          >
            <img
              class="partner-logo"
              :class="{ 'partner-logo-svg': logo.isSvg, 'partner-logo-invert': darkCards && logo.isBosch }"
              :src="logo.src"
              :alt="g === 1 ? logo.alt : ''"
              @load="scheduleRepeatCheck"
              @error="onImgError"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Itens padrão exibidos caso a API de Seções (gerenciador > Personalizar >
// Seções) esteja indisponível - mesmos logos da seção do pacote 2908_74.
// Caminhos absolutos para funcionar em qualquer rota (/, /products/ etc.).
// A logomarca "BOSCH Power Tools" é preta/branca: quando o quadro do parceiro
// é escuro (tema escuro) as cores dela são invertidas (preto vira branco).
function isBosch(...textos) {
  return /bosch/i.test(textos.filter(Boolean).join(" "));
}

const FALLBACK_LOGOS = [
  { src: "/assets/partners/bosch.png", alt: "Bosch" },
  { src: "/assets/partners/dewalt.png", alt: "DeWalt" },
  { src: "/assets/partners/makita.png", alt: "Makita" },
  { src: "/assets/partners/stanley.png", alt: "Stanley" },
  { src: "/assets/partners/tramontina.png", alt: "Tramontina" },
  { src: "/assets/partners/schulz.png", alt: "Schulz" },
  { src: "/assets/partners/milwaukee.svg", alt: "Milwaukee" },
  { src: "/assets/partners/vonder.svg", alt: "Vonder" },
  { src: "/assets/partners/irwin.svg", alt: "Irwin Tools" },
  { src: "/assets/partners/gedore.svg", alt: "Gedore" },
  { src: "/assets/partners/wesco.svg", alt: "Wesco" },
].map((l) => ({ ...l, isSvg: /\.svg(\?|$)/i.test(l.src), isBosch: isBosch(l.alt, l.src) }));

export default {
  name: "CarrouselParceiros",
  data() {
    return {
      logos: FALLBACK_LOGOS,
      enabled: true,
      // true quando o título inteiro (h3) recebeu um degradê pelo Personalizar:
      // nesse caso as duas partes deixam de ter fundo próprio e mostram o
      // degradê do título, contínuo de "Nossas" até "Parceiras".
      joint: false,
      // true quando o fundo dos quadros dos parceiros é escuro (ver checkJoint).
      darkCards: false,
      // quantas vezes o conjunto de logos se repete dentro de cada grupo do
      // trilho (calculado pela largura da tela - ver updateRepeat).
      repeat: 1,
    };
  },
  mounted() {
    this.loadParceiros();
    this.$nextTick(this.scheduleRepeatCheck);
    window.addEventListener("resize", this.scheduleRepeatCheck);
    window.addEventListener("load", this.scheduleRepeatCheck);
    if (typeof ResizeObserver !== "undefined") {
      this._repeatObserver = new ResizeObserver(this.scheduleRepeatCheck);
      if (this.$refs.viewport) this._repeatObserver.observe(this.$refs.viewport);
    }
    this.$nextTick(this.scheduleJointCheck);
    window.addEventListener("load", this.scheduleJointCheck);
    // Folhas de estilo (tema escuro/claro, regras do Personalizar e
    // pré-visualização ao vivo) podem chegar ou mudar depois da montagem.
    document.addEventListener("load", this.scheduleJointCheck, true);
    if (typeof MutationObserver !== "undefined") {
      this._jointObserver = new MutationObserver(this.scheduleJointCheck);
      this._jointObserver.observe(document.head, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["href", "media", "disabled"],
      });
    }
  },
  updated() {
    this.scheduleJointCheck();
    this.scheduleRepeatCheck();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.scheduleRepeatCheck);
    window.removeEventListener("load", this.scheduleRepeatCheck);
    if (this._repeatObserver) {
      this._repeatObserver.disconnect();
      this._repeatObserver = null;
    }
    if (this._repeatRaf) {
      cancelAnimationFrame(this._repeatRaf);
      this._repeatRaf = null;
    }
    window.removeEventListener("load", this.scheduleJointCheck);
    document.removeEventListener("load", this.scheduleJointCheck, true);
    if (this._jointObserver) {
      this._jointObserver.disconnect();
      this._jointObserver = null;
    }
    if (this._jointRaf) {
      cancelAnimationFrame(this._jointRaf);
      this._jointRaf = null;
    }
  },
  computed: {
    // Conjunto de logos repetido `repeat` vezes: é o conteúdo de UM grupo do
    // trilho. O trilho tem dois grupos iguais, então a animação (-50%) desloca
    // exatamente um grupo e recomeça sem nenhum espaço no meio.
    groupLogos() {
      const lista = [];
      for (let i = 0; i < this.repeat; i += 1) lista.push(...this.logos);
      return lista;
    },
    // A duração é proporcional ao número de repetições para manter a mesma
    // velocidade (px/s) de antes, mesmo quando o grupo precisa ser mais longo.
    trackStyle() {
      return { "--partners-repeat": this.repeat };
    },
  },
  methods: {
    scheduleRepeatCheck() {
      if (this._repeatRaf) return;
      this._repeatRaf = requestAnimationFrame(() => {
        this._repeatRaf = null;
        this.updateRepeat();
      });
    },
    // Mede a largura de um conjunto de logos e define quantas repetições são
    // necessárias para que cada grupo seja pelo menos tão largo quanto a área
    // visível (+ folga). Só aumenta/diminui quando o valor realmente muda.
    updateRepeat() {
      const viewport = this.$refs.viewport;
      const group = Array.isArray(this.$refs.group) ? this.$refs.group[0] : this.$refs.group;
      if (!viewport || !group || !this.logos.length) return;
      const groupWidth = group.getBoundingClientRect().width;
      const viewportWidth = viewport.getBoundingClientRect().width;
      if (!groupWidth || !viewportWidth) return;
      const baseWidth = groupWidth / this.repeat;
      if (!baseWidth) return;
      // Com grupo >= área visível, o trilho (2 grupos) sempre cobre a tela em
      // qualquer ponto da animação. Se imagens ainda carregando mudarem a
      // largura, o ResizeObserver/load disparam este cálculo de novo.
      const needed = Math.max(1, Math.ceil(viewportWidth / baseWidth));
      if (needed !== this.repeat) this.repeat = needed;
    },
    scheduleJointCheck() {
      if (this._jointRaf) return;
      this._jointRaf = requestAnimationFrame(() => {
        this._jointRaf = null;
        this.checkJoint();
      });
    },
    checkJoint() {
      const el = this.$refs.title;
      if (!el) return;
      let bg = "none";
      try {
        bg = window.getComputedStyle(el).backgroundImage || "none";
      } catch (e) {
        bg = "none";
      }
      const joint = bg !== "none";
      if (joint !== this.joint) this.joint = joint;
      // Quadro escuro? (cor de fundo já resolvida do primeiro quadro)
      const card = this.$el && this.$el.querySelector ? this.$el.querySelector(".partner-card") : null;
      if (card) {
        let dark = false;
        try {
          const m = String(window.getComputedStyle(card).backgroundColor || "").match(
            /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/i
          );
          if (m && (m[4] === undefined || parseFloat(m[4]) > 0.5)) {
            const lum = (0.2126 * m[1] + 0.7152 * m[2] + 0.0722 * m[3]) / 255;
            dark = lum < 0.5;
          }
        } catch (e) {
          dark = false;
        }
        if (dark !== this.darkCards) this.darkCards = dark;
      }
    },
    async loadParceiros() {
      try {
        const response = await fetch(
          "/api/sync_ecommerce_sections.php?secao=parceiros"
        );
        const data = await response.json();
        if (data && data.status === "success") {
          this.enabled = data.enabled !== false;
          if (Array.isArray(data.itens) && data.itens.length) {
            this.logos = data.itens.map((item) => ({
              src: item.image,
              alt: item.name || "Parceiro",
              isSvg: /\.svg(\?|$)/i.test(item.image || ""),
              isBosch: isBosch(item.name, item.image),
            }));
          }
        }
      } catch (error) {
        console.error("Erro ao carregar seção de Parceiros:", error);
        // mantém os logos padrão (FALLBACK_LOGOS) já definidos em data()
      }
    },
    onImgError(event) {
      // Evita ícone de "imagem quebrada" caso uma logomarca configurada no
      // gerenciador ainda não tenha sido enviada/publicada corretamente.
      event.target.style.display = "none";
    },
  },
};
</script>

<style scoped>
.partners-section {
  padding: 40px 0 20px;
  background: var(--products-section-bg, #f8f9fa);
}
.partners-header {
  text-align: center;
  margin-bottom: 30px;
  padding: 0 20px;
}
/* Título: o preenchimento do texto é "transparente" e cada parte pinta o
   próprio texto com um degradê (background-clip:text). Assim o Personalizar
   (Modo Edição) reconhece o título como texto com degradê e permite escolher
   duas cores - tanto em cada parte ("Nossas" / "Marcas Parceiras") quanto no
   título inteiro. Sem ajuste, o visual é o mesmo de antes (cor do tema +
   laranja). */
.partners-title {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--default-color, #1a1a1a);
  margin-bottom: 8px;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.partners-title-a,
.partners-title-b {
  -webkit-background-clip: text;
  background-clip: text;
  /* o preenchimento (-webkit-text-fill-color) é herdado do título */
}
.partners-title-a {
  background-image: linear-gradient(var(--default-color, #1a1a1a), var(--default-color, #1a1a1a));
}
.partners-title-b {
  background-image: linear-gradient(#FF6600, #FF6600);
}
/* Degradê aplicado ao título inteiro: as partes ficam sem fundo próprio para
   mostrar o degradê do título contínuo. Um ajuste feito direto numa das partes
   (Personalizar) continua valendo por cima. */
.partners-title.is-joint .partners-title-a,
.partners-title.is-joint .partners-title-b {
  background-image: none;
}
/* Navegadores sem suporte a background-clip:text: cores sólidas de antes. */
@supports not ((-webkit-background-clip: text) or (background-clip: text)) {
  .partners-title { -webkit-text-fill-color: currentColor; }
  .partners-title-b { color: #FF6600; }
}
.partners-subtitle {
  font-size: 0.95rem;
  color: var(--mx3-style-text-muted, #6c757d);
  margin: 0;
}

/* Container principal: centraliza e oculta o que está fora. */
.scrolling-logos {
  overflow: hidden;
  margin: 0 auto;
  max-width: 100%;
  padding: 10px 0 20px;
  position: relative;
}
.scrolling-logos::before,
.scrolling-logos::after {
  content: '';
  position: absolute;
  top: 0; bottom: 0;
  width: 80px;
  z-index: 2;
  pointer-events: none;
}
.scrolling-logos::before {
  left: 0;
  background: linear-gradient(to right, var(--products-section-bg, #f8f9fa), transparent);
}
.scrolling-logos::after {
  right: 0;
  background: linear-gradient(to left, var(--products-section-bg, #f8f9fa), transparent);
}

/* Trilho animado: width max-content + dois grupos idênticos (.logos-group)
   garantem que translateX(-50%) desloque exatamente um grupo, e o fim de um
   ciclo coincide com o início do próximo (loop contínuo, sem espaço). */
.logos-slide {
  display: flex;
  flex-wrap: nowrap;
  width: max-content;
  align-items: center;
  /* duração proporcional às repetições: mantém a velocidade constante */
  animation: scrollInfinito calc(35s * var(--partners-repeat, 1)) linear infinite;
  will-change: transform;
  backface-visibility: hidden;
}
.logos-group {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  align-items: center;
}
/* Pausa ao passar o mouse (mantida). */
.logos-slide:hover { animation-play-state: paused; }
.partner-card {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Cor editável em Personalizar > Fundo dos quadros dos parceiros
     (--partner-card-bg; claro: branco, escuro: cinza do tema). */
  background: var(--partner-card-bg, #ffffff);
  border: 1px solid rgba(128, 128, 128, 0.25);
  border-radius: 12px;
  padding: 12px 20px;
  min-width: 160px;
  height: 80px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}
.partner-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(255, 102, 0, 0.2);
  border-color: rgba(255, 102, 0, 0.3);
}
.partner-logo {
  object-fit: contain;
  max-width: 130px;
  max-height: 55px;
  width: auto;
  height: auto;
  opacity: 0.75;
  filter: grayscale(30%);
  transition: all 0.3s ease;
}
.partner-logo-svg {
  max-width: 140px;
  max-height: 55px;
  border-radius: 6px;
}
.partner-card:hover .partner-logo {
  opacity: 1;
  filter: grayscale(0%);
}
/* Bosch em quadro escuro: preto <-> branco invertidos. */
.partner-logo.partner-logo-invert,
.partner-card:hover .partner-logo.partner-logo-invert {
  filter: invert(1);
}
.partner-logo.partner-logo-invert { opacity: 0.9; }
.partner-card:hover .partner-logo.partner-logo-invert { opacity: 1; }

@media (min-width: 768px) and (max-width: 1199.98px) {
  .partners-section { padding: 24px 0 14px; }
  .partners-header { margin-bottom: 18px; }
  .scrolling-logos { padding: 6px 0 12px; }
  .partner-card { min-width: 130px; height: 68px; padding: 8px 16px; }
  .partner-logo { max-width: 105px; max-height: 46px; }
  .logos-slide { animation-duration: calc(24s * var(--partners-repeat, 1)); }
}

@media (max-width: 767.98px) {
  .partners-section { padding: 18px 0 10px; }
  .partners-header { margin-bottom: 12px; }
  .scrolling-logos { padding: 4px 0 8px; }
  .partner-card { min-width: 120px; height: 60px; padding: 7px 14px; }
  .partner-logo { max-width: 96px; max-height: 40px; }
  .logos-slide { animation-duration: calc(18s * var(--partners-repeat, 1)); }
}
</style>

<style>
@keyframes scrollInfinito {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
</style>
